import classNames from 'clsx';
import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type Ref,
} from 'react';
import { Button, type ButtonProps } from '../Button';
import { Grid } from '../Grid';

import { SnapState, useSnapState } from '../SnapPoint/context';
import { type ChildAndClassNameProps } from '../types';
import { H1Landing, H6 } from '../typography';
import {
  container,
  imageContainer,
  spaceFromSubtitle,
  styledImage,
  styledSubtitle,
  textContainer,
} from './LandingHeader.css';

export const LandingHeaderContainer = forwardRef(
  (
    { children, className, ...props }: ChildAndClassNameProps,
    ref: Ref<HTMLDivElement>,
  ) => (
    <Grid className={classNames(container, className)} ref={ref} {...props}>
      {children}
    </Grid>
  ),
);

LandingHeaderContainer.displayName = 'LandingHeaderContainer';

export const LandingTitle = forwardRef(
  (
    { children, className, ...props }: ChildAndClassNameProps,
    ref: Ref<HTMLHeadingElement>,
  ) => (
    <H1Landing className={className} ref={ref} {...props}>
      {children}
    </H1Landing>
  ),
);

LandingTitle.displayName = 'LandingTitle';

export const LandingSubtitle = forwardRef(
  (
    { children, className, ...props }: ChildAndClassNameProps,
    ref: Ref<HTMLHeadingElement>,
  ) => (
    <H6 className={classNames(styledSubtitle, className)} ref={ref} {...props}>
      {children}
    </H6>
  ),
);

LandingSubtitle.displayName = 'LandingSubtitle';

export const LandingDiscount = forwardRef(
  (
    { children, className, ...props }: ChildAndClassNameProps,
    ref: Ref<HTMLHeadingElement>,
  ) => (
    <H6 className={classNames(className)} ref={ref} {...props}>
      {children}
    </H6>
  ),
);

LandingDiscount.displayName = 'LandingDiscount';

export const LandingButton = forwardRef(
  (
    { children, className, ...props }: ButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => (
    <Button
      className={classNames(spaceFromSubtitle, className)}
      ref={ref}
      {...props}
    >
      {children}
    </Button>
  ),
);

LandingButton.displayName = 'LandingButton';

export interface LandingCopyContainerProps {
  children?: React.ReactNode;
  className?: string;
}

export const LandingCopyContainer = forwardRef(
  (props: LandingCopyContainerProps, ref: Ref<HTMLDivElement>) => {
    const { children, className, ...rest } = props;

    return (
      <div className={classNames(textContainer, className)} ref={ref} {...rest}>
        {children}
      </div>
    );
  },
);

LandingCopyContainer.displayName = 'LandingCopyContainer';

export const LandingImage = forwardRef(
  (
    {
      className,
      imgSrc,
      imgAlt,
      ...props
    }: {
      imgSrc: string;
      imgAlt: string;
      className?: string;
    },
    ref: Ref<HTMLImageElement>,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [isFirstFrame, setIsFirstFrame] = useState(true);

    useEffect(() => {
      setIsFirstFrame(false);
    }, []);

    const snapState = useSnapState();

    return (
      <div
        className={classNames(
          imageContainer({
            active:
              snapState !== SnapState.BEFORE &&
              snapState !== undefined &&
              !isFirstFrame,
          }),
          className,
        )}
        ref={containerRef}
        {...props}
      >
        <img
          className={styledImage({
            active:
              snapState !== SnapState.BEFORE &&
              snapState !== undefined &&
              !isFirstFrame,
          })}
          src={imgSrc}
          alt={imgAlt}
          ref={ref}
          {...props}
        />
      </div>
    );
  },
);

LandingImage.displayName = 'LandingImage';

export const LandingVideo = forwardRef(
  (
    {
      className,
      vidSrc,
      vidAlt,
      ...props
    }: {
      vidSrc: string;
      vidAlt: string;
      className?: string;
    },
    ref: Ref<HTMLDivElement>,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [isFirstFrame, setIsFirstFrame] = useState(true);

    useEffect(() => {
      setIsFirstFrame(false);
    }, []);

    const snapState = useSnapState();

    return (
      <div
        className={classNames(
          imageContainer({
            active:
              snapState !== SnapState.BEFORE &&
              snapState !== undefined &&
              !isFirstFrame,
          }),
          className,
        )}
        ref={ref || containerRef}
        {...props}
      >
        <div
          style={{
            width: '90vw',
            maxWidth: '1400px',
            position: 'relative',
            left: '50%',
            right: '50%',
            marginLeft: 'max(-45vw, -700px)',
            marginRight: 'max(-45vw, -700px)',
            aspectRatio: '16/9',
            borderRadius: '16px',
            overflow: 'hidden',
          }}
        >
          <iframe
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              borderRadius: '16px',
            }}
            src={vidSrc}
            title={vidAlt}
            allow="autoplay; encrypted-media; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    );
  },
);

LandingVideo.displayName = 'LandingVideo';
